import React, {useCallback, useEffect, useState} from "react";
import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    FormControlLabel,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import {fetchRole, setSelectedRole, updateRole} from "../../Redux/Action";
import {setModal} from "../../../../Common/App";
import {AddRoleModal, RoleDeleteModal} from "../../index";
import Checkbox from "@mui/material/Checkbox";
import {FormCard, Loader} from "../../../../../Components";
import AddIcon from "@mui/icons-material/Add";
import {AddPermissionModal} from "../../../Permission/Components/Modals/AddPermissionModal";

export const ViewRole = () => {

    const dispatch = useDispatch();
    const item = useSelector((store) => store.roles.selected);
    const permissions = useSelector((store) => store.permissions.list);
    const [, setErrors] = useState({});
    const [, setMessage] = useState("");
    const [, setStatus] = useState("info");
    const [isLoading, setIsLoading] = useState(false);
    const [selectAll, setSelectAll] = useState({});
    const [checkedPermissions, setCheckedPermissions] = useState({});

    const [data, setData] = useState({
        role_name: "",
        description: "",
        permissions: [],
    });

    useEffect(() => {
        if(!item) return;
        setData({
            id: item?.id || "",
            role_name: item?.role_name || "",
            description: item?.description || "",
            permissions: item?.permissions || [],
        })
    }, [item]);

    const fetchRoleData = useCallback(async () => {
        if (!item?.id) return;
        setIsLoading(true);
        await fetchRole({ dispatch, filters:{id: item.id} }).then((res) => {
            setIsLoading(false);
            setErrors(res.errors || {});
            setMessage(res.message);
            setStatus(res.status);
        });
    },[dispatch, item?.id]);

    useEffect(() => {
        fetchRoleData()
    }, [fetchRoleData]);

    useEffect(() => {
        if (!item?.permissions) return;

        const initialChecked = {};
        const initialSelectAll = {};

        permissions.forEach(permission => {
            initialChecked[permission.id] = item.permissions.includes(permission.id);
        });

        const grouped = permissions.reduce((acc, permission) => {
            acc[permission.group] = acc[permission.group] || [];
            acc[permission.group].push(permission.id);
            return acc;
        }, {});

        for (const [group, ids] of Object.entries(grouped)) {
            initialSelectAll[group] = ids.every(id => initialChecked[id]);
        }

        setCheckedPermissions(initialChecked);
        setSelectAll(initialSelectAll);
    }, [item?.permissions, permissions]);

    const handleChange = (e) => {
        setData((prevData) => ({
            ...prevData,
            [e.target.name]: e.target.value,
        }));
    };

    const handlePermissionChange = (permissionId) => {
        setCheckedPermissions(prevState => ({
            ...prevState,
            [permissionId]: !prevState[permissionId]
        }));
    };

    const handleSelectAll = (groupName) => {
        const newCheckedPermissions = { ...checkedPermissions };
        const newSelectAll = { ...selectAll };
        permissions.forEach(permission => {
            if (permission.group === groupName) {
                newCheckedPermissions[permission.id] = !newSelectAll[groupName];
            }
        });
        setCheckedPermissions(newCheckedPermissions);
        newSelectAll[groupName] = !newSelectAll[groupName];
        setSelectAll(newSelectAll);
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        const selectedPermissions = Object.keys(checkedPermissions).filter(id => checkedPermissions[id]);
        await updateRole({ dispatch, data: { ...data, permissions: selectedPermissions } }).then((res) => {
            if(res.success){
            }
            setIsLoading(false);
            setErrors(res.errors)
            setMessage(res.message);
            setStatus(res.status);
        })
    };

    const groupedPermissions = permissions.reduce((acc, permission) => {
        if (!acc[permission.group]) {
            acc[permission.group] = [];
        }
        acc[permission.group].push(permission);
        return acc;
    }, {});

    const handleDelete = (item) => () => {
        dispatch(setModal({ component: <RoleDeleteModal roleId={item.id} />, open: true}));
    }

    return (
        <>
            {Object.keys(item).length > 0 ? (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormCard>
                            <CardHeader
                                title={"Role Details"}
                                avatar={<Loader isLoading={isLoading} type={"linear"} />}
                            />
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Role Name"
                                            name="role_name"
                                            onChange={(e) => handleChange(e)}
                                            variant="outlined"
                                            size="large"
                                            value={data?.role_name}
                                            InputLabelProps={{ shrink: true }}
                                            helperText={item?.error?.role_name}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Role Description"
                                            name="description"
                                            onChange={(e) => handleChange(e)}
                                            variant="outlined"
                                            size="large"
                                            value={data?.description}
                                            InputLabelProps={{ shrink: true }}
                                            helperText={item?.error?.description}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                            <CardActions sx={{width: '100%', display: 'flex', justifyContent: 'space-between'}}>
                                <Stack direction={"row"} spacing={2}>
                                    <Button variant="contained" color="secondary" onClick={ () => dispatch(setSelectedRole({}))}>Cancel</Button>
                                    {item.type !== 'SYSTEM' && <Button variant={"standard"} color={"error"} onClick={handleDelete(item)}>Delete</Button>}
                                </Stack>
                                <Button variant="contained" color="primary" onClick={handleSubmit} disabled={isLoading}>Save</Button>
                            </CardActions>
                        </FormCard>
                    </Grid>

                    {Object.entries(groupedPermissions).map(([groupName, permissionsInGroup]) => (
                        <Grid item xs={12} key={groupName}>
                            <Card>
                                <CardHeader
                                    sx={{ '& .MuiTypography-root':{textTransform:'capitalize'} }}
                                    title={groupName}
                                    action={
                                        <Stack direction={"row"} spacing={2}>
                                            <IconButton
                                                onClick={() => dispatch(setModal({ component: <AddPermissionModal groupName={groupName} permissionsInGroup={permissionsInGroup} />, open: true }))}
                                            >
                                                <AddIcon />
                                            </IconButton>
                                            <FormControlLabel
                                                sx={{
                                                    '& .MuiFormControlLabel-label': { fontSize: '0.8rem' },
                                                }}
                                                control={<Checkbox />}
                                                label={selectAll[groupName] ? "Deselect All" : "Select All"}
                                                onChange={() => handleSelectAll(groupName)}
                                                //checked={selectAll && permissionsInGroup.every(permission => checkedPermissions[permission.id])}
                                                checked={!!selectAll[groupName]} // ✅ Fixed
                                            />
                                        </Stack>
                                    }
                                />
                                <CardContent>
                                    <Grid container spacing={2}>
                                        {permissionsInGroup.map((permission) => (
                                            <Grid item xs={12} md={6} key={permission.id}>
                                                <FormControlLabel
                                                    sx={{
                                                        '& .MuiFormControlLabel-label': { fontSize: '0.75rem' },
                                                    }}
                                                    control={
                                                        <Checkbox
                                                            //checked={checkedPermissions[permission.id]}
                                                            checked={!!checkedPermissions[permission.id]} // ✅ Fixed
                                                            onChange={() => handlePermissionChange(permission.id)}
                                                        />
                                                    }
                                                    label={permission.label}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Card>
                            <CardHeader title={"Role Details"} />
                            <CardContent sx={{ flexDirection: "column", display: "flex", justifyContent: "center", alignItems: "center", paddingBottom:'1rem!important' }}>
                                <Stack direction={"column"} spacing={2}>
                                    <Typography>Please select a role from the list</Typography>
                                    <Button variant="contained" color="primary" onClick={() => dispatch(setModal({ component: <AddRoleModal />, open: true }))}>
                                        Add New Role
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}
        </>
    );
};
